import React from 'react';
import RouteGuard from '../guards/RouteGuard';
import PrivateRouteGuard from '../guards/PrivateRoute';
import PublicRoute from '../guards/PublicRoute';
import GuestRoute from '../guards/GuestRoute';
import PermissionRoute from '../guards/PermissionRoute';
import RoleRoute from '../guards/RoleRoute';
import FeatureFlagRoute from '../guards/FeatureFlagRoute';
import SSORoute from '../guards/SSORoute';
import { useAuthCheck } from './useAuthCheck';
import { ROUTES } from '@lsgo/lsgo-common/constants/routes';

/**
 * Internal wrapper component that applies auth check before rendering guard
 * This ensures auth is always checked first for private/access routes
 */
const RouteBuilderWithAuth = ({ requiresAuth, allowExpired, embed, redirectTo, children }) => {
    const authRedirect = useAuthCheck({ requiresAuth, allowExpired, embed, redirectTo });

    if (authRedirect) {
        return authRedirect;
    }

    return children;
};

/**
 * High-level route configuration builder
 * Acts as a wrapper that selects the appropriate guard based on configuration
 * Maintains backward compatibility with current PrivateRoute API
 *
 * @param {string} type - Route type: 'private', 'public', 'guest', 'sso', 'permission', 'role', 'featureFlag'
 * @param {boolean} requiresAuth - Whether authentication is required
 * @param {string[]} permissions - Required permissions
 * @param {boolean} requireAllPermissions - If true, requires ALL permissions (AND), otherwise ANY (OR)
 * @param {number[]} roles - Required roles
 * @param {string[]} featureFlags - Required feature flags
 * @param {boolean} requireAllFlags - If true, requires ALL flags (AND), otherwise ANY (OR)
 * @param {string} ssoType - SSO type: 'sentral' | 'schoolBytes'
 * @param {string} redirectTo - Redirect path if access denied
 * @param {ReactNode} fallback - Fallback component to render if access denied
 * @param {boolean} embed - Public route mode (no auth required)
 * @param {boolean} allowExpired - Allow expired accounts
 * @param {ReactNode} children - Child components to render
 * @param {function} render - Render function (alternative to children)
 */
const RouteBuilder = ({
    type = 'private',
    requiresAuth,
    permissions,
    requireAllPermissions = false,
    roles,
    featureFlags,
    requireAllFlags = false,
    ssoType,
    redirectTo,
    fallback,
    embed = false,
    allowExpired = false,
    children,
    render,
    // Legacy props for backward compatibility
    routeType,
    isAuthenticated,
    accountExpired,
    redirect,
    ...props
}) => {
    // Handle legacy routeType prop for backward compatibility
    if (routeType && !type) {
        switch (routeType) {
            case 'guest':
                type = 'guest';
                break;
            case 'sentral':
            case 'schoolBytes':
                type = 'sso';
                ssoType = routeType;
                break;
            case 'private':
            default:
                type = 'private';
                break;
        }
    }

    // Handle legacy redirect prop
    if (redirect && !redirectTo) {
        redirectTo = redirect;
    }

    // Handle legacy accountExpired prop
    if (accountExpired !== undefined && allowExpired === false) {
        allowExpired = !accountExpired;
    }

    // Determine which guard to use based on type
    switch (type) {
        case 'public':
            return (
                <PublicRoute render={render} {...props}>
                    {children}
                </PublicRoute>
            );

        case 'guest':
            return (
                <GuestRoute redirectTo={redirectTo || ROUTES.TEACHER.DASHBOARD.path} render={render} {...props}>
                    {children}
                </GuestRoute>
            );

        case 'sso':
            return (
                <SSORoute ssoType={ssoType} redirectTo={redirectTo} render={render} {...props}>
                    {children}
                </SSORoute>
            );

        case 'permission':
            return (
                <RouteBuilderWithAuth
                    requiresAuth={true}
                    allowExpired={allowExpired}
                    embed={embed}
                    redirectTo={redirectTo}
                >
                    <PermissionRoute
                        permissions={permissions}
                        requireAll={requireAllPermissions}
                        redirectTo={redirectTo}
                        fallback={fallback}
                        render={render}
                        {...props}
                    >
                        {children}
                    </PermissionRoute>
                </RouteBuilderWithAuth>
            );

        case 'role':
            return (
                <RouteBuilderWithAuth
                    requiresAuth={true}
                    allowExpired={allowExpired}
                    embed={embed}
                    redirectTo={redirectTo}
                >
                    <RoleRoute roles={roles} redirectTo={redirectTo} fallback={fallback} render={render} {...props}>
                        {children}
                    </RoleRoute>
                </RouteBuilderWithAuth>
            );

        case 'featureFlag':
            return (
                <RouteBuilderWithAuth
                    requiresAuth={true}
                    allowExpired={allowExpired}
                    embed={embed}
                    redirectTo={redirectTo}
                >
                    <FeatureFlagRoute
                        flags={featureFlags}
                        requireAll={requireAllFlags}
                        redirectTo={redirectTo}
                        fallback={fallback}
                        render={render}
                        {...props}
                    >
                        {children}
                    </FeatureFlagRoute>
                </RouteBuilderWithAuth>
            );

        case 'private':
        default:
            // Use RouteGuard for complex cases (multiple checks), PrivateRoute for simple auth-only
            if (permissions?.length || roles?.length || featureFlags?.length) {
                return (
                    <RouteGuard
                        requiresAuth={requiresAuth !== false}
                        permissions={permissions}
                        requireAllPermissions={requireAllPermissions}
                        roles={roles}
                        featureFlags={featureFlags}
                        requireAllFlags={requireAllFlags}
                        redirectTo={redirectTo}
                        fallback={fallback}
                        embed={embed}
                        allowExpired={allowExpired}
                        render={render}
                        {...props}
                    >
                        {children}
                    </RouteGuard>
                );
            }

            // Simple private route - just authentication
            return (
                <PrivateRouteGuard
                    redirectTo={redirectTo}
                    allowExpired={allowExpired}
                    embed={embed}
                    render={render}
                    {...props}
                >
                    {children}
                </PrivateRouteGuard>
            );
    }
};

export default RouteBuilder;
